import * as THREE from 'three'
import type { Point } from './xrTeleportGeometry'

/** Scene positions are relative to the Minecraft eye; the blink follows the XR camera. */
export class XRTeleportVisual {
  private readonly geometry = new THREE.BufferGeometry()
  private readonly positions = new Float32Array(81 * 3)
  private readonly line = new THREE.Line(this.geometry, new THREE.LineBasicMaterial({ color: 0x73e9a7 }))
  private readonly ring = new THREE.Mesh(new THREE.RingGeometry(0.22, 0.29, 40), new THREE.MeshBasicMaterial({ color: 0x73e9a7, side: THREE.DoubleSide, depthWrite: false }))
  private readonly blink = new THREE.Mesh(new THREE.PlaneGeometry(20, 20), new THREE.MeshBasicMaterial({ color: 0, transparent: true, opacity: 0, depthTest: false, depthWrite: false }))
  private readonly pointer = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xa9eaff, depthTest: false, transparent: true, opacity: 0.75 }))
  private readonly pointerPositions = new Float32Array(6)

  constructor(scene: THREE.Scene, camera: THREE.Camera) {
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3).setUsage(THREE.DynamicDrawUsage))
    this.pointer.geometry.setAttribute('position', new THREE.BufferAttribute(this.pointerPositions, 3).setUsage(THREE.DynamicDrawUsage))
    this.line.frustumCulled = false; this.pointer.frustumCulled = false
    this.ring.rotation.x = -Math.PI / 2
    this.line.visible = false; this.ring.visible = false; this.pointer.visible = false
    this.pointer.renderOrder = 10900
    scene.add(this.line, this.ring, this.pointer)
    this.blink.position.z = -0.15; this.blink.renderOrder = 12000; camera.add(this.blink)
  }

  update(points: Point[], target: Point | null, valid: boolean, eye: Point, aiming: boolean, opacity: number) {
    this.line.visible = aiming; this.ring.visible = aiming && !!target
    this.blink.material.opacity = opacity
    this.blink.visible = opacity > 0
    if (!aiming) return
    const color = valid ? 0x73e9a7 : 0xff6868
    this.line.material.color.setHex(color); this.ring.material.color.setHex(color)
    const count = Math.min(points.length, 81)
    for (let i = 0; i < count; i++) {
      this.positions[i * 3] = points[i].x - eye.x; this.positions[i * 3 + 1] = points[i].y - eye.y; this.positions[i * 3 + 2] = points[i].z - eye.z
    }
    this.geometry.attributes.position.needsUpdate = true
    this.geometry.setDrawRange(0, count)
    if (target) this.ring.position.set(target.x - eye.x, target.y - eye.y + 0.015, target.z - eye.z)
  }

  uiRay(origin: Point, direction: Point, visible: boolean) {
    this.pointer.visible = visible
    if (!visible) return
    this.pointerPositions.set([origin.x, origin.y, origin.z, origin.x + direction.x * 2, origin.y + direction.y * 2, origin.z + direction.z * 2])
    this.pointer.geometry.attributes.position.needsUpdate = true
  }

  hide() { this.line.visible = false; this.ring.visible = false; this.pointer.visible = false; this.blink.visible = false }

  dispose() {
    for (const object of [this.line, this.ring, this.blink, this.pointer]) { object.removeFromParent(); object.geometry.dispose(); object.material.dispose() }
  }
}
